import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  LinearProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Download,
  Security,
  CheckCircle,
  FileCopy,
  Timeline,
  People,
  Assessment,
  Score,
  Archive,
  CloudDownload,
  InsertDriveFile
} from '@mui/icons-material';

// Custom CSV icon component


const exportTypes = [
  { 
    name: 'Session Records', 
    icon: <Timeline />, 
    description: 'Complete session logs with timestamps',
    recordCount: '1,245 records',
    size: '2.4 MB',
    lastUpdated: 'Today, 10:30 AM'
  },
  { 
    name: 'Student Reach', 
    icon: <People />, 
    description: 'Student enrollment and participation data',
    recordCount: '5,432 students',
    size: '4.1 MB',
    lastUpdated: 'Today, 09:15 AM'
  },
  { 
    name: 'Attendance', 
    icon: <Assessment />, 
    description: 'Daily attendance records with percentages',
    recordCount: '45,678 entries',
    size: '3.2 MB',
    lastUpdated: 'Yesterday, 11:45 PM'
  },
  { 
    name: 'Test Scores', 
    icon: <Score />, 
    description: 'Complete assessment scores and analytics',
    recordCount: '12,847 scores',
    size: '5.6 MB',
    lastUpdated: 'Today, 08:00 AM'
  },
];

const complianceFeatures = [
  'Real-time data extraction',
  'Automated CSV formatting',
  'GDPR & FERPA compliant',
  'Digital audit trail',
  'Encrypted downloads'
];

const AuditComplianceSection = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleExport = (type) => {
    setExporting(type);
    setLoading(true);
    
    // Simulate export process
    setTimeout(() => {
      setLoading(false);
      setExporting(null);
      setSnackbar({
        open: true,
        message: `${type} CSV downloaded successfully!`,
        severity: 'success'
      });
    }, 1500);
  };

  const handleExportAll = () => {
    setExporting('all');
    setLoading(true);
    
    // Simulate bulk export process
    setTimeout(() => {
      setLoading(false);
      setExporting(null);
      setSnackbar({
        open: true,
        message: 'All compliance reports downloaded as ZIP containing CSV files!',
        severity: 'success'
      });
    }, 2500);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Paper>
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: 'primary.light', 
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CloudDownload fontSize="medium" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                Instant Audit Compliance
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Generate comprehensive CSV reports with one click. Perfect for school audits, compliance reviews, and data analysis.
              </Typography>
            </Box>
            <Chip 
              icon={<Security fontSize="small" />} 
              label="CSV READY" 
              color="success"
              size="small"
              variant="outlined"
            />
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Features List */}
          <Box>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
              COMPLIANCE FEATURES
            </Typography>
            <Grid container spacing={1}>
              {complianceFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: 'success.main', fontSize: 24 }} />
                    <Typography variant="body2" color="text.secondary">
                      {feature}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Export Types */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
              AVAILABLE CSV REPORTS
            </Typography>
            <Grid container spacing={2}>
              {exportTypes.map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      borderColor: exporting === item.name ? 'primary.main' : 'divider',
                      bgcolor: exporting === item.name ? 'primary.50' : 'background.paper',
                      transition: 'all 0.2s',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {exporting === item.name && (
                      <LinearProgress 
                        sx={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 2
                        }} 
                      />
                    )}
                    
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            p: 1, 
                            borderRadius: 1.5, 
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                            display: 'flex'
                          }}>
                            {item.icon}
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {item.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FileCopy />
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Stack direction="row" spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            {item.recordCount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.size}
                          </Typography>
                        </Stack>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Download fontSize="small" />}
                          onClick={() => handleExport(item.name)}
                          disabled={loading}
                          sx={{ 
                            minWidth: 'auto',
                            px: 1.5,
                            py: 0.5,
                            fontSize: '0.75rem'
                          }}
                        >
                          Export
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Footer Note */}
          <Typography variant="body2" color="text.secondary" align="center">
            All CSV exports are automatically formatted for immediate use in Excel, Google Sheets, and other analysis tools.
          </Typography>
        </Stack>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AuditComplianceSection;